import {AfterViewChecked, ChangeDetectorRef, Component, Input, OnChanges, OnInit, OnDestroy, Signal} from '@angular/core';
import {AudioRecorderService, RecordingStoppedEvent} from "@store/file-upload/audio-recorder.service";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {BehaviorSubject, EMPTY, of, switchMap, takeUntil, Subject} from "rxjs";
import {TranslocoModule} from "@ngneat/transloco";
import {ImageUpload} from "../../../../../../models/questions/ImageUpload";
import {AudioUploadService, IAudioUpload} from "@store/file-upload/audio-upload.service";
import {SurveyViewerService} from "../../../services/survey-viewer.service";
import {AbstractControl} from "@angular/forms";
import {IQuestionComponent} from "../../../../../../interfaces/IQuestionComponent";
import {MatTooltipModule} from "@angular/material/tooltip";
import {PipesModule} from "../../../../../../pipes/pipes.module";

@Component({
    selector: 'app-audio-recorder',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        NgIf,
        AsyncPipe,
        TranslocoModule,
        MatTooltipModule,
        NgForOf,
        PipesModule
    ],
    templateUrl: './audio-recorder.component.html',
    styleUrl: './audio-recorder.component.scss'
})
export class AudioRecorderComponent implements IQuestionComponent, OnInit, AfterViewChecked, OnDestroy {
  errorMessages: Record<string, {fr: string, en: string}> = {
    noRecordingValidator: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    },
    noTextCanBeHeard: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    },
    uploadFailed: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    }
  }

  isRecording = new BehaviorSubject<boolean>(false);
  private isStopping = false; // Flag pour éviter les appels multiples à stop()
  audioBlob?: Blob;
  audioBlobUrl = new BehaviorSubject<string>(undefined);
  @Input() isSmallScreen: boolean;
  @Input() activeLang: string;
  @Input() question: ImageUpload;

  questionFormControl: AbstractControl<any, any>;
  audioFormControl: AbstractControl<any, any>;
  commentsFormControl: AbstractControl<any, any>;

  audioUrl = '';

  hasError = new BehaviorSubject<{fr: string, en: string}[]>([]);
  hasWarning = new BehaviorSubject<{fr: string, en: string}[]>([]);
  isTouched = new BehaviorSubject<boolean>(false);
  private hasAttemptedRecording = false; // Track if user has attempted to record
  private destroy$ = new Subject<void>();

  constructor(
    private recorder: AudioRecorderService,
    public audioUploadService: AudioUploadService,
    public surveyViewerService: SurveyViewerService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {
    // Écouter les arrêts automatiques d'enregistrement
    this.recorder.onRecordingStopped
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (event: RecordingStoppedEvent) => {
        if (event.qKey === this.question.key) {
          // L'enregistrement a été arrêté automatiquement (changement de page)
          // Vérifier si un enregistrement était en cours (même si isRecording est déjà false)
          const wasRecording = this.isRecording.value;
          
          // Toujours mettre à jour l'état d'enregistrement
          this.isRecording.next(false);
          
          // Si un enregistrement était en cours et qu'on a un blob, lancer la validation
          if (wasRecording && event.blob) {
            try {
              // Initialiser les contrôles s'ils ne le sont pas encore
              if (!this.audioFormControl || !this.questionFormControl) {
                this.initializeFormControls();
              }

              this.audioBlob = event.blob;
              this.audioBlobUrl.next(URL.createObjectURL(this.audioBlob));
              this.hasAttemptedRecording = true;
              if (this.commentsFormControl) {
                this.commentsFormControl.setValue('Invalid');
              }
              this.upload();
            } catch (error) {
              console.error('Error handling automatic stop:', error);
              this.audioBlob = undefined;
              this.audioBlobUrl.next(undefined);
            }
          } else if (wasRecording && !event.blob) {
            // Si un enregistrement était en cours mais qu'on n'a pas de blob, réinitialiser l'état
            this.audioBlob = undefined;
            this.audioBlobUrl.next(undefined);
          }
        }
      });
  }

  ngAfterViewChecked() {
    this.audioUploadService.uploadedAudio$[this.question.key] ??= new BehaviorSubject<IAudioUpload>(null);
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key);
    this.audioFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName).get(this.question.statementsOrder[0]);
    this.commentsFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.commentsControlName).get(this.question.statementsOrder[0]);
    this.isTouched.next(this.audioFormControl?.touched || false);
    if(this.audioFormControl?.value){
      this.audioUrl = this.audioFormControl.value;
      this.audioBlobUrl.next(this.audioFormControl.value)
      // Si le contenu est valide au chargement, marquer comme touché pour afficher le crochet
      if(this.audioFormControl.valid && !this.audioFormControl.touched){
        this.audioFormControl.markAsTouched();
        this.isTouched.next(true);
        this.questionFormControl.markAsTouched();
      }
      this.cdr.detectChanges();
    }
    this.audioUploadService.uploadedAudio$[this.question.key]
      .pipe(switchMap((d) => d && (d.url !== this.audioUrl) ? of(d) : EMPTY))
      .subscribe({
        next: (audioData) => {
          // Toujours garder l'audio même si la transcription est invalide
          this.audioUrl = audioData?.url ?? '';

          if (!audioData.transcription?.length) {
            this.hasAttemptedRecording = true;
            const warningMessage = [this.errorMessages.noTextCanBeHeard];
            // Si la question est optionnelle, afficher en warning (jaune) au lieu d'erreur
            if (this.question.isOptional) {
              this.hasWarning.next(warningMessage);
              this.hasError.next([]);
            } else {
              this.hasError.next(warningMessage);
              this.hasWarning.next([]);
            }
            this.audioBlob = undefined;
            this.audioBlobUrl.next(undefined);
            this.audioUrl = '';
            this.audioUploadService.uploadedAudio$[this.question.key].next(null);
            this.audioFormControl.setValue(null);
            this.audioFormControl.setErrors({noTextCanBeHeard: true});
            this.audioFormControl.markAsTouched();
            this.isTouched.next(true);
            this.audioFormControl.markAsDirty();
            this.audioFormControl.updateValueAndValidity({emitEvent: true});
            this.commentsFormControl.setValue('');
          }else{
            this.hasWarning.next([]);
            this.audioUrl = audioData?.url ?? '';
            this.audioFormControl.setValue(this.audioUrl);
            this.commentsFormControl.setValue(audioData.transcription);
            this.audioFormControl.setErrors(null);
            this.audioFormControl.markAsTouched();
            this.audioFormControl.updateValueAndValidity({emitEvent: true});
            this.hasError.next([]);

            // Mettre à jour l'URL de l'audio blob si disponible
            if (this.audioUrl && !this.audioBlobUrl.value) {
              this.audioBlobUrl.next(this.audioUrl);
            }
          }
        },
        error: () => {
          this.hasAttemptedRecording = true;
          const warningMessage = [this.errorMessages.uploadFailed];
          // Si la question est optionnelle, afficher en warning (jaune) au lieu d'erreur
          if (this.question.isOptional) {
            this.hasWarning.next(warningMessage);
            this.hasError.next([]);
          } else {
            this.hasError.next(warningMessage);
            this.hasWarning.next([]);
          }
          this.audioBlob = undefined;
          this.audioBlobUrl.next(undefined);
          this.audioUrl = '';
          this.audioUploadService.uploadedAudio$[this.question.key].next(null);
          this.audioFormControl.setValue(null);
          this.audioFormControl.setErrors({uploadFailed: true});
          this.audioFormControl.markAsTouched();
          this.isTouched.next(true);
          this.audioFormControl.markAsDirty();
          this.commentsFormControl.setValue('');
          this.audioFormControl.updateValueAndValidity({emitEvent: true});
        }
      });
    this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName)
      .get(this.question.statementsOrder[0]).statusChanges.subscribe(value => {
      if (value === 'VALID') {
        this.surveyViewerService.activeSurvey$.value.form.get(this.question.key).markAsTouched()
        this.hasError.next([]);
        this.hasWarning.next([]);
      } else {
        // Only show errors if the control is touched or all form controls are marked as touched
        if (this.audioFormControl.touched || this.surveyViewerService.allFormControlsMarkedAsTouched$.value) {
          this.updateErrorMessages();
        }
      }
    })
    this.audioFormControl.statusChanges.subscribe(() => {
      this.isTouched.next(this.audioFormControl?.touched || false);
    });
    this.surveyViewerService.allFormControlsMarkedAsTouched$.subscribe(() => {
      this.updateErrorMessages();
    });
    this.surveyViewerService.activeSurvey$.subscribe(() => {this.updateErrorMessages()});
  }

  private updateErrorMessages() {
    // Only show errors if the control is touched or all form controls are marked as touched
    if (this.audioFormControl?.touched || this.surveyViewerService.allFormControlsMarkedAsTouched$.value) {
      if (this.audioFormControl?.errors) {
        const errorMessages = Object.entries(this.audioFormControl.errors).map(([key, val]) => {
          if (val) {
            return this.errorMessages[key] || this.errorMessages.noRecordingValidator;
          }
        }).filter((msg): msg is {fr: string, en: string} => Boolean(msg));

        // Si la question est optionnelle et qu'un enregistrement a été tenté, afficher en warning
        if (this.question.isOptional && this.hasAttemptedRecording) {
          this.hasWarning.next(errorMessages);
          this.hasError.next([]);
        } else {
          this.hasError.next(errorMessages);
          this.hasWarning.next([]);
        }
      } else if (this.audioFormControl?.valid) {
        this.hasError.next([]);
        this.hasWarning.next([]);
      }
    } else {
      // Don't show errors if the control hasn't been touched yet
      this.hasError.next([]);
      this.hasWarning.next([]);
    }
  }

  async start() {
    // Réinitialiser le flag d'arrêt
    this.isStopping = false;
    this.isRecording.next(true);
    await this.recorder.startRecording(this.question.key);
    this.audioFormControl.markAsUntouched();
    this.isTouched.next(false);
  }

  async stop() {
    // Vérifier qu'un enregistrement est vraiment en cours et qu'on n'est pas déjà en train d'arrêter
    if (!this.isRecording.value || this.isStopping) {
      return;
    }

    this.isStopping = true;
    this.isRecording.next(false);
    this.audioBlob = await this.recorder.stopRecording(this.question.key);
    this.audioBlobUrl.next(URL.createObjectURL(this.audioBlob));
    this.hasAttemptedRecording = true;
    this.commentsFormControl.setValue('Invalid');
    this.upload();
  }

  eraseAudio() {
    // Annuler l'upload en cours s'il y en a un pour éviter le chevauchement
    if (this.audioUploadService.isUploading$[this.question.key]?.value === true) {
      this.audioUploadService.cancelUpload(this.question.key);
    }

    this.audioBlob = undefined;
    this.audioBlobUrl.next(undefined);
    this.audioUploadService.uploadedAudio$[this.question.key]?.next(null);
    this.audioUploadService.isUploading$[this.question.key]?.next(false);
    this.audioFormControl.setValue(null);
    this.audioFormControl.setErrors({noRecordingValidator: true});
    this.audioFormControl.markAsTouched();
    this.isTouched.next(true);
    this.commentsFormControl.setValue('');
    this.hasWarning.next([]);
    this.audioFormControl.updateValueAndValidity({emitEvent: true});
    this.updateErrorMessages();
  }

  upload() {
    if (!this.audioBlob) return;

    // Initialiser les contrôles s'ils ne le sont pas encore
    if (!this.audioFormControl || !this.questionFormControl) {
      this.initializeFormControls();
    }

    if (!this.audioFormControl || !this.questionFormControl) {
      console.error('Form controls not available for upload');
      return;
    }

    this.hasError.next([]);
    this.hasWarning.next([]);
    const provisionalValue = this.audioBlobUrl.value || 'uploading';
    this.audioFormControl.setValue(provisionalValue);
    this.audioFormControl.markAsDirty();
    this.audioFormControl.setErrors(null);
    this.audioFormControl.markAsTouched();
    this.isTouched.next(true);
    this.questionFormControl.markAsTouched();
    this.audioFormControl.updateValueAndValidity({emitEvent: true});
    const sourceLanguage = this.activeLang || 'fr';
    this.audioUploadService.uploadAudioFile(this.audioBlob, this.question.key, this.surveyViewerService.activeSurvey$.value.id, sourceLanguage);
  }

  private initializeFormControls() {
    try {
      if (this.surveyViewerService.activeSurvey$.value?.form) {
        this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key);
        if (this.questionFormControl) {
          this.audioFormControl = this.questionFormControl.get(this.question.rowsOrder[0])?.get(this.question.audioControlName)?.get(this.question.statementsOrder[0]);
          this.commentsFormControl = this.questionFormControl.get(this.question.rowsOrder[0])?.get(this.question.commentsControlName)?.get(this.question.statementsOrder[0]);
        }
      }
    } catch (error) {
      console.error('Error initializing form controls:', error);
    }
  }

  async ngOnDestroy() {
    // Si un enregistrement est en cours lors de la destruction du composant (changement de page),
    // arrêter l'enregistrement et démarrer la validation automatiquement
    // Note: stopAllRecordingsOnCurrentPage() devrait déjà avoir arrêté l'enregistrement,
    // mais on vérifie ici au cas où
    if (this.isRecording.value && this.recorder.isRecording(this.question.key)) {
      try {
        // Initialiser les contrôles s'ils ne le sont pas encore
        if (!this.audioFormControl || !this.questionFormControl) {
          this.initializeFormControls();
        }

        // Arrêter l'enregistrement et démarrer la validation
        this.isRecording.next(false);
        this.audioBlob = await this.recorder.stopRecording(this.question.key);
        if (this.audioBlob) {
          this.audioBlobUrl.next(URL.createObjectURL(this.audioBlob));
          if (this.commentsFormControl) {
            this.commentsFormControl.setValue('Invalid');
          }
          this.upload();
        }
      } catch (error) {
        // Si l'arrêt échoue (peut-être déjà arrêté par stopRecordingIfActive), on nettoie quand même l'état
        console.warn('Recording may have already been stopped:', error);
        this.isRecording.next(false);
      }
    }
    
    // Nettoyer les subscriptions après avoir géré l'enregistrement
    this.destroy$.next();
    this.destroy$.complete();
  }

}
